import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { responseLogWithNotification, getPaginationOpts, useUpdatePeriod } from '../../../utils/utils';
import { getPaymentData } from '../../PaymentLog/reducer';
import '../../EventLog/components/event-log-page.scss';
import { sessionLogColumns } from '../constants';
import { getColumnsForTable } from '../../../utils/getColumnsForTable';
import { loadPaymentList } from '../../PaymentLog/actions';
import defaultCarImg from '../../../theme/img/default-car.png';
import { getSystemData } from '../../System/reducer';
import { getSessionData } from '../reducer';
import ImagePreviewModal from '../../../components/base/ImagePreviewModal';
import VideoPreviewModal from '../../../components/base/VideoPreviewModal';
import VehicleRegistrationPlate from '../../../components/base/VehicleRegistrationPlate/VehicleRegistrationPlate';
import SessionClientEditModal from './SessionClientEditModal';
import { dateOrDateTimeFormatter, paymentAmountFormatter, paymentMethodFormatter } from '../../../utils/formatters';
import resolveSessionMediaUrl from '../../../utils/resolveSessionMediaUrl';
import { createRecoveryCode } from '../actions';
import { successNotification } from '../../Notifications/notificationDecorator';

export default function SessionLogStatus() {
  const dispatch = useDispatch();
  const basicKeys = ['id', 'reason', 'type', 'status', 'client_id', 'ended_at', 'started_at'];
  const [previewImage, setPreviewImage] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [showClientEditModal, setShowClientEditModal] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const [recoveryCodeLoading, setRecoveryCodeLoading] = useState(false);

  const { session } = useSelector(getSessionData);
  const { paymentList, paymentListMeta } = useSelector(getPaymentData);
  const { updatePeriod } = useSelector(getSystemData);

  const entryImage = resolveSessionMediaUrl(session?.client?.entry_image || session?.entry_image || session?.client?.entry_image_path || session?.entry_image_path || null);
  const exitImage = resolveSessionMediaUrl(session?.client?.exit_image || session?.exit_image || session?.client?.exit_image_path || session?.exit_image_path || null);
  const entryVideo = resolveSessionMediaUrl(session?.client?.entry_video || session?.entry_video || session?.client?.entry_video_path || session?.entry_video_path || null);
  const exitVideo = resolveSessionMediaUrl(session?.client?.exit_video || session?.exit_video || session?.client?.exit_video_path || session?.exit_video_path || null);

  const client = session?.client || {};
  const clientId = client.id || session?.client_id;

  const hasClientValue = value => value !== null && typeof value !== 'undefined' && value !== '';

  const getSessionRecoveryCode = currentSession => (
    currentSession?.recovery_code
    || currentSession?.recoveryCode
    || currentSession?.meta?.recovery_code
    || currentSession?.meta?.recoveryCode
    || null
  );

  const entryIdentifierLabels = {
    CARD_ID: 'Карта',
    TICKET_ID: 'Разовый талон',
    LPN: 'Государственный регистрационный знак',
    RFID: 'RFID',
    QR: 'QR-код',
    QR_CODE: 'QR-код',
    BARCODE: 'Штрихкод',
    CARD: 'Карта',
    TICKET: 'Разовый талон',
    SUBSCRIPTION: 'Абонемент',
    card_id: 'Карта',
    ticket_id: 'Разовый талон',
    lpn: 'Государственный регистрационный знак',
    card: 'Карта',
    ticket: 'Разовый талон',
    subscription: 'Абонемент',
    rfid: 'RFID',
    qr: 'QR-код',
    qr_code: 'QR-код',
    barcode: 'Штрихкод',
  };

  const vehicleTypeLabels = {
    CAR: 'Легковой',
    TRUCK: 'Грузовой',
    BUS: 'Автобус',
    MOTORCYCLE: 'Мотоцикл',
    car: 'Легковой',
    truck: 'Грузовой',
    bus: 'Автобус',
    motorcycle: 'Мотоцикл',
  };

  const getReadableEnumValue = (value, map) => {
    if (!hasClientValue(value)) {
      return value;
    }

    return map[value] || value;
  };

  const normalizeEntryIdentifierLabel = value => {
    if (!hasClientValue(value)) {
      return value;
    }

    const normalized = String(value).trim().toUpperCase();
    if (normalized === 'LPN' || normalized === 'ГРЗ' || normalized === 'ГОСУДАРСТВЕННЫЙ РЕГИСТРАЦИОННЫЙ ЗНАК') {
      return 'Государственный регистрационный знак';
    }

    if (normalized === 'TICKET' || normalized === 'ТАЛОН' || normalized === 'РАЗОВЫЙ ТАЛОН') {
      return 'Разовый талон';
    }

    if (normalized === 'CARD' || normalized === 'КАРТА') {
      return 'Карта';
    }

    if (normalized === 'SUBSCRIPTION' || normalized === 'АБОНЕМЕНТ') {
      return 'Абонемент';
    }

    return value;
  };

  const lpnValue = client.lpn || session?.lpn;
  const ticketId = client.ticket_id || client.ticket_number;
  const cardId = client.card_id || client.masked_pan;
  const entryIdentifierValue = normalizeEntryIdentifierLabel(
    client.entry_identifier_label || getReadableEnumValue(client.entry_identifier, entryIdentifierLabels),
  );
  const vehicleTypeValue = client.vehicle_type_label || getReadableEnumValue(client.vehicle_type, vehicleTypeLabels);

  const lpnPlateItem = {
    lpn: lpnValue,
    lpn_type: client.lpn_type || session?.lpn_type || 'ru',
    vehicle_type: client.vehicle_type || session?.vehicle_type || 'car',
    lpn_parsed: client.lpn_parsed || session?.lpn_parsed || '',
  };

  const clientEntityRows = [
    ...(hasClientValue(cardId) ? [{ key: 'card_id', label: 'Карта', value: cardId }] : []),
    ...(hasClientValue(ticketId) ? [{ key: 'ticket_id', label: 'Разовый талон', value: ticketId }] : []),
    ...(hasClientValue(entryIdentifierValue)
      ? [{ key: 'entry_identifier', label: 'Основной идентификатор', value: entryIdentifierValue }]
      : []),
    ...(hasClientValue(vehicleTypeValue)
      ? [{ key: 'vehicle_type', label: 'Транспортное средство', value: vehicleTypeValue }]
      : []),
    ...(hasClientValue(lpnValue)
      ? [{
        key: 'lpn',
        label: 'Государственный Регистрационный Знак',
        value: lpnValue,
        render: () => <VehicleRegistrationPlate item={lpnPlateItem} />,
      }]
      : []),
  ];

  const paymentFieldConfig = [
    {
      key: 'paid_at',
      label: 'Дата, время оплаты',
      getValue: payment => payment?.paid_at,
      format: value => dateOrDateTimeFormatter(value),
    },
    {
      key: 'amount',
      label: 'Сумма оплаты, руб',
      getValue: payment => payment?.amount,
      format: value => paymentAmountFormatter(value),
    },
    {
      key: 'method_label',
      label: 'Способ оплаты',
      getValue: payment => paymentMethodFormatter(payment?.method_label, payment),
    },
    {
      key: 'type_label',
      label: 'Тип платежа',
      getValue: payment => payment?.type_label || payment?.type,
    },
    {
      key: 'masked_pan',
      label: 'Номер карты',
      getValue: payment => payment?.masked_pan,
    },
    {
      key: 'rrn',
      label: 'RRN',
      getValue: payment => payment?.rrn,
    },
    {
      key: 'uip',
      label: 'УИП',
      getValue: payment => payment?.uip,
    },
    {
      key: 'auth_code',
      label: 'Код авторизации',
      getValue: payment => payment?.auth_code,
    },
  ];

  const paymentRows = (Array.isArray(paymentList) ? paymentList : []).map((payment, index) => {
    const rows = paymentFieldConfig
      .map(field => {
        const rawValue = field.getValue(payment);
        if (!hasClientValue(rawValue)) {
          return null;
        }

        return {
          key: field.key,
          label: field.label,
          value: field.format ? field.format(rawValue, payment) : rawValue,
        };
      })
      .filter(Boolean);

    return {
      key: payment?.id || `payment-${index}`,
      rows,
    };
  }).filter(item => item.rows.length > 0);

  const renderEntityValue = value => {
    if (value === null || typeof value === 'undefined' || value === '') {
      return 'Нет данных';
    }

    return value;
  };

  const renderBasicInformationValue = column => {
    const value = session[column.dataField];

    if (column.dataField === 'client_id') {
      return renderEntityValue(value);
    }

    return column.formatter ? column.formatter(value, session) : value;
  };

  const openPreview = (imageUrl, title) => {
    if (!imageUrl) {
      return;
    }
    setPreviewImage(imageUrl);
    setPreviewTitle(title);
  };

  const openVideoPreview = (videoUrl, title) => {
    if (!videoUrl) {
      return;
    }
    setPreviewVideo(videoUrl);
    setPreviewTitle(title);
  };

  const closePreview = () => {
    setPreviewImage(null);
    setPreviewVideo(null);
    setPreviewTitle('');
  };

  const handleOpenClientCard = () => {
    if (!hasClientValue(clientId)) {
      return;
    }

    setShowClientEditModal(true);
  };

  const handleCreateRecoveryCode = () => {
    if (!hasClientValue(session?.id) || recoveryCodeLoading) {
      return;
    }

    setRecoveryCodeLoading(true);
    dispatch(createRecoveryCode({ id: session.id }))
      .then(data => {
        const code = data?.code || data?.data?.code || null;
        setRecoveryCode(code);
        successNotification('Код для утерянного талона', code ? `Сгенерирован код ${code}` : 'Код сгенерирован');
      })
      .catch(response => responseLogWithNotification(response, 'Код для утерянного талона', 'Не удалось сгенерировать код'))
      .finally(() => setRecoveryCodeLoading(false));
  };

  const handleLoadingPaymentList = opts => {
    const paginationOpts = getPaginationOpts(paymentListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: { session_id: session.id },
    };
    return dispatch(loadPaymentList(requestData)).catch(responseLogWithNotification);
  };

  const loadData = () => handleLoadingPaymentList();
  const sessionRecoveryCode = getSessionRecoveryCode(session);

  const [setPeriod, destroy] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => {
    setPeriod(updatePeriod);
    return destroy;
  }, [updatePeriod]);

  useEffect(() => {
    setRecoveryCode(sessionRecoveryCode);
    setRecoveryCodeLoading(false);
  }, [session?.id, sessionRecoveryCode]);

  return (
    <div className="slp-status">
      <div className="slp-basic-information">
        <div className="slp-basic-photo-list">
          <div className="slp-basic-photo-item">
            <div className="slp-basic-photo-title">Фото въезда</div>
            <div
              className={classNames('slp-basic-photo-image', { clickable: Boolean(entryImage) })}
              style={{ backgroundImage: `url(${entryImage || defaultCarImg})` }}
              onClick={() => openPreview(entryImage, 'Фото въезда')}
            />
          </div>
          <div className="slp-basic-photo-item">
            <div className="slp-basic-photo-title">Фото выезда</div>
            <div
              className={classNames('slp-basic-photo-image', { clickable: Boolean(exitImage) })}
              style={{ backgroundImage: `url(${exitImage || defaultCarImg})` }}
              onClick={() => openPreview(exitImage, 'Фото выезда')}
            />
          </div>
        </div>
        <span className="slp-basic-information-title">Основная информация</span>
        <div className="slp-basic-information-box">
          {getColumnsForTable(sessionLogColumns, basicKeys).map((column, index) => (
            <div className="slp-basic-information-box-item" key={index}>
              <div className="slp-basic-information-box-item-title">{column.text}</div>
              <div className="slp-basic-information-box-item-content">
                {renderBasicInformationValue(column)}
              </div>
            </div>
          ))}
          <div className="slp-basic-information-box-item slp-recovery-code-item">
            <div className="slp-basic-information-box-item-title">Код для утерянного талона</div>
            <div className="slp-basic-information-box-item-content slp-recovery-code-cell">
              <div className="slp-recovery-code-content">
                <span className={classNames('slp-recovery-code-value', { empty: !recoveryCode })}>
                  {recoveryCode || 'Не сгенерирован'}
                </span>
                <button
                  type="button"
                  className="slp-recovery-code-button"
                  disabled={recoveryCodeLoading || !hasClientValue(session?.id)}
                  onClick={handleCreateRecoveryCode}
                >
                  {recoveryCodeLoading ? 'Генерация...' : recoveryCode ? 'Обновить' : 'Сгенерировать'}
                </button>
              </div>
            </div>
          </div>
          {entryVideo ? (
            <div className="slp-basic-information-box-item">
              <div className="slp-basic-information-box-item-title">Видео въезда</div>
              <div className="slp-basic-information-box-item-content">
                <button type="button" className="slp-basic-information-video-link" onClick={() => openVideoPreview(entryVideo, 'Видео въезда')}>
                  Смотреть
                </button>
              </div>
            </div>
          ) : null}
          {exitVideo ? (
            <div className="slp-basic-information-box-item">
              <div className="slp-basic-information-box-item-title">Видео выезда</div>
              <div className="slp-basic-information-box-item-content">
                <button type="button" className="slp-basic-information-video-link" onClick={() => openVideoPreview(exitVideo, 'Видео выезда')}>
                  Смотреть
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className="slp-status-table-box">
        <div className="slp-status-tables-container">
          <div className="slp-client-entity">
            <div className="slp-client-entity-head">
              <div className="slp-client-entity-title">Клиент</div>
              {hasClientValue(clientId) ? (
                <div className="slp-client-entity-open-card" onClick={handleOpenClientCard}>Редактировать карточку клиента</div>
              ) : null}
            </div>
            <div className="frame slp-client-entity-frame">
              <div className="sticky-table-wrapper custom-scroll-theme">
                <table className="table">
                  <thead className="data-table-header">
                    <tr className="table-header-row">
                      <th className="table-header-cell slp-client-field-column">Поле</th>
                      <th className="table-header-cell slp-client-value-column">Значение</th>
                    </tr>
                  </thead>
                  <tbody className="data-table-content">
                    {clientEntityRows.map(item => (
                      <tr className="data-table-row" key={item.key}>
                        <td className="data-table-cell slp-client-field-column">{item.label}</td>
                        <td className="data-table-cell slp-client-value-column">{item.render ? item.render() : renderEntityValue(item.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="slp-status-tables-container-title">
            <div className="slp-status-title">Платеж</div>
          </div>
          <div className="slp-payments-list">
            {paymentRows.length ? paymentRows.map((payment, index) => (
              <div className="slp-client-entity slp-payment-entity" key={payment.key}>
                {paymentRows.length > 1 ? <div className="slp-payment-entity-title">Платеж {index + 1}</div> : null}
                <div className="frame slp-client-entity-frame">
                  <div className="sticky-table-wrapper custom-scroll-theme">
                    <table className="table">
                      <thead className="data-table-header">
                        <tr className="table-header-row">
                          <th className="table-header-cell slp-client-field-column">Поле</th>
                          <th className="table-header-cell slp-client-value-column">Значение</th>
                        </tr>
                      </thead>
                      <tbody className="data-table-content">
                        {payment.rows.map(row => (
                          <tr className="data-table-row" key={`${payment.key}-${row.key}`}>
                            <td className="data-table-cell slp-client-field-column">{row.label}</td>
                            <td className="data-table-cell slp-client-value-column">{renderEntityValue(row.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )) : (
              <div className="slp-client-entity slp-payment-entity">
                <div className="frame slp-client-entity-frame">
                  <div className="sticky-table-wrapper custom-scroll-theme">
                    <table className="table">
                      <thead className="data-table-header">
                        <tr className="table-header-row">
                          <th className="table-header-cell slp-client-field-column">Поле</th>
                          <th className="table-header-cell slp-client-value-column">Значение</th>
                        </tr>
                      </thead>
                      <tbody className="data-table-content">
                        <tr className="data-table-row">
                          <td className="data-table-cell slp-client-field-column" colSpan={2}>Нет данных</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ImagePreviewModal open={Boolean(previewImage)} imageUrl={previewImage} title={previewTitle} onClose={closePreview} />
      <VideoPreviewModal open={Boolean(previewVideo)} videoUrl={previewVideo} title={previewTitle} onClose={closePreview} />
      {showClientEditModal ? <SessionClientEditModal showClientEditModal={showClientEditModal} setShowClientEditModal={setShowClientEditModal} client={{ ...client, id: clientId }} sessionId={session?.id} /> : null}
    </div>
  );
}
